import {BaseComponent} from '../baseComponent';
import * as React from 'react';
import Modal from 'react-modal';
import {tracked} from '../../decorators/tracked';
import {Server} from '../../services/server';
import {lazyInject} from '../../services/container';
import {HttpError} from '../../errors/httpError';
import {BAD_REQUEST, CONFLICT, FORBIDDEN, UNAUTHORIZED} from 'http-status-codes';
import {Facebook} from '../../services/facebook';
import {bind} from '../../decorators/bind';
import {History} from 'history';
import {Translate} from '../../services/translate';
import classNames from 'classnames';
import {Field, Form, Formik, FormikActions, FormikProps} from 'formik';
import {boolean, object, setLocale, string} from 'yup';
import tickIcon from '../../assets/images/tick_icon.svg';
import fbIcon from '../../assets/images/fb_icon_blue.jpg';

enum ModalState
{
    // Loading indicator
    Loading,
    // Need to log in to facebook notice
    Notice,
    // Facebook login was canceled
    Canceled,
    // Not all needed permissions were granted
    NotGranted,
    // User has no email on facebook
    NoEmail,
    // There was an error
    Error,
    // No tickets left
    NoTickets,
    // Already signed up for this ticket
    AlreadySignedUp,
    // User has email on facebook, but we need them to confirm it
    ConfirmEmail
}

interface ReserveTicketModalProps
{
    history: History;
    onDismiss: () => void;
    showModal?: boolean;
}

interface ReserveFormValues
{
    email: string;
    name: string;
    birthdate: string;
    terms: boolean;
}

export class ClubcardSignupModal extends BaseComponent<ReserveTicketModalProps>
{
    @tracked
    private modalState: ModalState = ModalState.Loading;
    
    @tracked
    private emailInputValue: string = '';

    @tracked
    private termsInputValue: any = false;
    
    @lazyInject(Server)
    private server: Server;
    
    @lazyInject(Facebook)
    private facebook: Facebook;
    
    @lazyInject(Translate)
    private translate: Translate;
    
    private fbName: string;
    
    public constructor(props: ReserveTicketModalProps, context: any)
    {
        super(props, context);

        setLocale({
            mixed: {
                default: this.translate.translate('formikMixedDefault') as any as string,
                required: this.translate.translate('formikMixedRequired') as any as string
            },
            string: {
                email: this.translate.translate('formikStringEmail') as any as string,
                min: this.translate.translate('formikStringMin') as any as string
            }
        });
    }

    public componentWillReceiveProps(nextProps: Readonly<ReserveTicketModalProps>, nextContext: any): void
    {
        this.modalState = ModalState.Loading;
    }

    public render(): React.ReactNode
    {
        return <Modal
            isOpen={!!this.props.showModal}
            className={{
                base: `modal-content`,
                afterOpen: 'modal-content-after-open',
                beforeClose: 'modal-content-before-close'
            }}
            overlayClassName={{
                base: `modal-overlay modal-overlay-${ModalState[this.modalState].toLowerCase()} clubcard-modal-overlay`,
                afterOpen: 'modal-overlay-after-open',
                beforeClose: 'modal-overlay-before-close'
            }}
            onAfterOpen={this.onAfterModalOpen}
        >
            {this.renderContent()}
        </Modal>;
    }
    
    private renderContent(): React.ReactNode
    {
        switch (this.modalState)
        {
            case ModalState.Loading:
                return <div className="sk-circle">
                    <div className="sk-circle1 sk-child" />
                    <div className="sk-circle2 sk-child" />
                    <div className="sk-circle3 sk-child" />
                    <div className="sk-circle4 sk-child" />
                    <div className="sk-circle5 sk-child" />
                    <div className="sk-circle6 sk-child" />
                    <div className="sk-circle7 sk-child" />
                    <div className="sk-circle8 sk-child" />
                    <div className="sk-circle9 sk-child" />
                    <div className="sk-circle10 sk-child" />
                    <div className="sk-circle11 sk-child" />
                    <div className="sk-circle12 sk-child" />
                </div>;
            case ModalState.Notice:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('reserveNotice')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.onLoginClick}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.Canceled:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('canceledFacebookLogin')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.props.onDismiss}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.NotGranted:
                return null;
            case ModalState.ConfirmEmail:
            case ModalState.NoEmail:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className={classNames({'reserve-text': true, 'confirm-email': this.modalState === ModalState.ConfirmEmail})}>
                        <p>
                            {this.translate.translate(
                                this.modalState === ModalState.NoEmail ? 'enterEmail' :
                                    ('confirmEmailClubcard'), { name: this.fbName })
                            }
                        </p>
                    </div>
                    <Formik<ReserveFormValues>
                        initialValues={{
                            email: this.emailInputValue,
                            name: this.fbName,
                            birthdate: '',
                            terms: false
                        }}
                        validationSchema={object({
                            email: string().required().email(),
                            name: string().required(),
                            birthdate: string().required().min(6),
                            terms: boolean().oneOf([true], this.translate.translate('formikTerms') as any as string)
                        })}
                        onSubmit={this.onFormSubmit}
                        render={this.formRender}
                    />
                </>;
            case ModalState.Error:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('bookingError')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.props.onDismiss}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.NoTickets:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('noTicketsLeft')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.onRefreshPage}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.AlreadySignedUp:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('alreadyBookedClubcard')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.onRefreshPage}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;                
            default:
                return null;
        }
    }
    
    @bind
    public async onAfterModalOpen(): Promise<void>
    {
        // await this.trySignup();
        await this.facebookLogin(false);
    }
    
    @bind
    private async onLoginClick(): Promise<void>
    {
        await this.facebookLogin(false);
    }
    
    private async facebookLogin(reauthorize: boolean): Promise<void>
    {
        try
        {
            this.modalState = ModalState.Loading;
            // let response = await this.facebook.getLoginStatus();
            
            if (!this.facebook.lastAuthResponse)
            {
                await this.facebook.login(reauthorize);
            }
    
            console.debug('facebook.login response', this.facebook.lastAuthResponse);
    
            if (!this.facebook.lastAuthResponse)
            {
                this.modalState = ModalState.Canceled;
                return;
            }
    
            // const grantedScopes = (response.authResponse.grantedScopes || '').split(',');
            //
            // if (grantedScopes.indexOf('email') < 0)
            // {
            //     // this.modalState = ModalState.NotGranted;
            //     this.emailInputValue = '';
            //     this.modalState = ModalState.NoEmail;
            //     return;
            // }
    
            const meResponse = await this.facebook.me(['name', 'email']);
            
            // if (!meResponse.email)
            // {
                this.fbName = meResponse.name;
                this.emailInputValue = meResponse.email || '';
                this.modalState = meResponse.email ? ModalState.ConfirmEmail : ModalState.NoEmail;
            //     return;
            // }
    
            // await this.tryLogin();
            // await this.trySignup();
        }
        catch (err)
        {
            this.modalState = ModalState.Error;
            console.error(err);
            throw err;
        }
    }
    
    private async trySignup(values: ReserveFormValues): Promise<void>
    {
        try
        {
            await this.server.post(`clubcard-signups`, values);
        }
        catch (err)
        {
            if (err instanceof HttpError)
            {
                // Try to show a detailed error
                switch (err.response.status)
                {
                    case FORBIDDEN:
                    case UNAUTHORIZED:
                        // Wait for api to load, so we can synchronously call Fb.login from the button click event. Otherwise it
                        // won't work
                        await this.facebook.promise;
                        this.modalState = ModalState.Notice;
                        return;
                    case BAD_REQUEST:
                    {
                        const responseBody = await err.response.json();

                        if (responseBody && responseBody.message === 'No tickets left')
                        {
                            this.modalState = ModalState.NoTickets;
                            return;
                        }
                        break;
                    }
                    case CONFLICT:
                    {
                        const responseBody = await err.response.json();

                        if (responseBody && responseBody.message === 'Already signed up')
                        {
                            this.modalState = ModalState.AlreadySignedUp;
                            return;
                        }
                        break;
                    }
                }
            }

            // Fallback to generic error
            this.modalState = ModalState.Error;
            throw err;
        }

        this.props.history.push(this.translate.formatLink('/clubcard/sikeres-igenyles'));
    }
    
    private async tryLogin(response?: facebook.StatusResponse, email?: string): Promise<void>
    {
        this.modalState = ModalState.Loading;
        
        if (!response)
        {
            response = await this.facebook.getLoginStatus();
        }

        await this.server.post(`login`, {
            provider: 'facebook',
            token: response.authResponse.accessToken,
            email
        });
    }
    
    @bind
    private onRefreshPage()
    {
        this.modalState = ModalState.Loading;
        location.reload();
    }
    
    @bind
    private formRender({touched, errors, isSubmitting}: FormikProps<ReserveFormValues>)
    {
        return <Form>
            <div className="reserve-input-wrapper">
                <Field type="text" name="name" placeholder={this.translate.translate('namePlaceholder') as any as string} />
                {touched.name && errors.name && <div className="input-errors">{errors.name}</div>}
                <Field type="text" name="birthdate" placeholder={this.translate.translate('birthdatePlaceholder')} />
                {touched.birthdate && errors.birthdate && <div className="input-errors">{errors.birthdate}</div>}
                <div className="reserve-text confirm-email">
                    <div style={{fontSize: '.6em'}}>
                        <p>{this.translate.translate('clubcardRealEmailNote')}</p>
                        <p>{this.translate.translate('freemailNote')}</p>
                    </div>
                </div>
                <Field type="email" name="email" placeholder={this.translate.translate('emailPlaceholder')} />
                {touched.email && errors.email && <div className="input-errors">{errors.email}</div>}
            </div>
            <div className="terms">
                <label>
                    <Field type="checkbox" name="terms" />
                    <span>{this.translate.translate('acceptTerms')}</span>
                </label>
            </div>
            {touched.terms && errors.terms && <div className="input-errors">{errors.terms}</div>}
            <div className="submit-button-wrapper">
                <button
                    type="submit"
                    className="reserve-ok-button"
                    disabled={isSubmitting}
                >
                    <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                    <img src={tickIcon} alt=""/>
                </button>
            </div>
        </Form>;
    }
    
    @bind
    private async onFormSubmit(values: ReserveFormValues, {setSubmitting}: FormikActions<ReserveFormValues>)
    {
        setSubmitting(true);
        
        try
        {
            await this.tryLogin(undefined, this.emailInputValue);
            await this.trySignup(values);
        }
        catch (err)
        {
            this.modalState = ModalState.Error;
            console.error(err);
            throw err;
        }
        finally
        {
            setSubmitting(false);
        }
    }
}